import "../styles/tailwind.css"
import { createFetcher } from "@tokenterminal/core/api"
import { ConsentBanner } from "@tokenterminal/core/consent/ConsentBanner"
import { PerformanceConsentGate } from "@tokenterminal/core/consent/PerformanceConsentGate"
import { ClickToComponent } from "@tokenterminal/core/ui/click-to-component"
import { SiteSchemas } from "@tokenterminal/core/ui/seo/SiteSchemas"
import { GeistMono, GeistSans } from "@tokenterminal/core/ui/theme/fonts"
import { ThemeProvider } from "@tokenterminal/core/ui/theme/ThemeContext"
import { Analytics } from "@vercel/analytics/next"
import { SpeedInsights } from "@vercel/speed-insights/next"
import type { AppProps as NextAppProps } from "next/app"
import Head from "next/head"
import {
  type ComponentType,
  Fragment,
  memo,
  type ReactElement,
  useRef,
} from "react"
import { AppErrorBoundary } from "@/components/AppErrorBoundary"
import { DefaultCanonical } from "@/components/common/Seo/PageMeta"
import { GTM } from "@/components/utils/gtm"
import { PostHogInit } from "@/components/utils/posthog"
import { APP_VERSION, TT_DEFAULT_JWT } from "@/constants/app.mjs"
import { TanStackQueryClientProvider } from "@/providers/TanStackQueryClientProvider"

function noop(_font: string) {}

type PageProps = Record<string, unknown>

type NextPageWithLayout = NextAppProps<PageProps>["Component"] & {
  getLayout?: (page: ReactElement, args: unknown) => ReactElement
  v3?: boolean
}

type Props = NextAppProps<PageProps>

interface InnerProps extends Props {
  Component: NextPageWithLayout
}

let apiUrl = "https://api-internal.tokenterminal.com"

if (process.env.NEXT_PUBLIC_USE_LOCAL_SERVER === `true`) {
  apiUrl = "http://localhost:3001"
}

if (
  process.env.NEXT_PUBLIC_VERCEL_ENV === `preview` ||
  globalThis?.origin?.endsWith(".tokenterminal.dev")
) {
  apiUrl = "https://api-internal.tokenterminal.dev"
}

function App(props: InnerProps): ReactElement {
  const { Component, pageProps } = props

  if (Component.v3) {
    if (Component.getLayout) {
      return Component.getLayout(<Component {...pageProps} />, {})
    }

    return <Component {...pageProps} />
  }

  // https://nextjs.org/docs/basic-features/layouts
  if (Component.getLayout) {
    return Component.getLayout(<Component {...pageProps} />, pageProps)
  }

  return <Component {...pageProps} />
}

const AppHead = memo(
  function AppHead() {
    return (
      <Head>
        <link rel="preconnect" href={apiUrl} />

        <title>Token Terminal | Fundamentals for crypto</title>

        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, user-scalable=no"
        />
        <meta
          name="facebook-domain-verification"
          content="141k7s1zlavs1vznks3nonb513g4f1"
        />
        <meta name="theme-color" content="#000000" />
        <meta
          name="description"
          content="Measure and evaluate Blockchains and Dapps through traditional financial metrics. Token Terminal is a Crypto Analytics Platform with Advanced Metrics & Tools."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@tokenterminal" />
        <meta
          name="twitter:title"
          content="Token Terminal"
          key="twitter:title"
        />
        <meta
          name="twitter:description"
          key="twitter:description"
          content="Fundamentals for crypto."
        />
        <meta
          name="twitter:image"
          key="twitter:image"
          content="https://tokenterminal.com/thumbnail.png"
        />
        <meta
          property="og:image"
          key="og:image"
          content="https://tokenterminal.com/thumbnail.png"
        />
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
    )
  },
  () => true
)

// Fix for local development - SSRProvider doesn't seem to work correctly
// Need to check if SSRProvider
const Provider = Fragment

// No need to use react context for this. Just use global variable
globalThis.isServerOrFirstHydration = true

globalThis.fetchApi = createFetcher({
  origin: apiUrl,
  appVersion: APP_VERSION,
  rateLimitJwt: TT_DEFAULT_JWT,
  defaultApiToken: process.env.NEXT_PUBLIC_API_DEFAULT_TOKEN,
})

const GTM_ID = "GTM-W3K5VQF"
const INITIAL_GTM_VALUES = { app: "landing", version: APP_VERSION }

function withContext(Component: ComponentType<InnerProps>) {
  noop(GeistSans.variable)
  noop(GeistMono.variable)

  return function AppWithContext({ pageProps, ...props }: Props) {
    const renderCount = useRef(0)
    // A render means the page/route changed
    if (globalThis.isServerOrFirstHydration && renderCount.current++ > 0) {
      globalThis.isServerOrFirstHydration = false
    }

    return (
      <TanStackQueryClientProvider>
        <ClickToComponent editor="cursor" projectRoot="apps/landing" />
        <Provider>
          <ThemeProvider>
            <AppHead />
            <DefaultCanonical />
            <SiteSchemas />
            <PerformanceConsentGate>
              <GTM id={GTM_ID} initialValues={INITIAL_GTM_VALUES} />
            </PerformanceConsentGate>
            <PostHogInit />
            <AppErrorBoundary>
              <Component {...props} pageProps={pageProps} />
            </AppErrorBoundary>
            <ConsentBanner />
            <Analytics />
            <SpeedInsights />
          </ThemeProvider>
        </Provider>
      </TanStackQueryClientProvider>
    )
  }
}

export default withContext(App)
